import { ReactNode, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Theme } from '@emotion/react'
import * as CSS from 'csstype'
import { FusionInterfaceZIndex } from '../../theme/FusionStyle'

const L2InterfaceRoot = styled.div({
	width: '100vw',
	maxWidth: '100vw',
	display: 'flex',
	flexDirection: 'column',
	// scrollable
})

const L2PrintInterfaceRoot = styled.div(({theme}) => ({
	// [ISSUE-550]
	// The only way that L4Page Breaks work in print layout is if they are
	// not encased in another box.  display: contents does not create a box
	// like 'flex' would.
	// https://developer.mozilla.org/en-US/docs/Web/CSS/display
	display: 'contents',
	// cause fonts to be legible when printing from headless Chrome
	// on Linux container
	fontFamily: 'sans-serif',
	'input': {
		fontFamily: 'sans-serif',
	},
}))

interface L2FixedInterfaceRootProps {
	fixedHeight: boolean
}

const L2FixedInterfaceRoot = styled.div<L2FixedInterfaceRootProps>(({fixedHeight}) => ({
	position: 'fixed',
	top: 0,
	left: 0,
	width: '100vw',
	maxWidth: '100vw',
	height: fixedHeight ? '100vh' : 'initial',
	maxHeight: fixedHeight ? '100vh' : 'initial',
	display: 'grid',
	gridTemplateColumns: '1fr',
	gridTemplateRows: 'max-content 1fr',
	zIndex: FusionInterfaceZIndex.Interface,
}))

const L2InterfaceHeader = styled.div({
	display: 'flex',
	flexDirection: 'column',
})

const L2PrintInterfaceHeader = styled.div(({theme}) => ({
	marginBottom: theme.spacing(4),
}))

type L2InterfaceHeaderShadowProps = {
	height: CSS.Property.Height
} & { theme: Theme }

const L2InterfaceHeaderShadow = styled.div(({theme, height}: L2InterfaceHeaderShadowProps) => ({
	height,
	marginBottom: theme.spacing(4),
}))

export enum L2InterfaceMode {
	Scrollable = 'scrollable',
	FixedHeight = 'fixedHeight',
	Print = 'print',
}

interface L2InterfaceProps {
	mode: L2InterfaceMode
	statusBar: ReactNode
	header: ReactNode
	children: ReactNode
}

const L2Interface = ({mode, statusBar, header, children}: L2InterfaceProps) => {
	const headerRef = useRef<HTMLDivElement>(null)
	const [headerShadowHeight, setHeaderShadowHeight] = useState<CSS.Property.Height>(0)
	useEffect(() => {
		let headerHeight = headerRef.current?.offsetHeight 
		setHeaderShadowHeight(headerHeight ? headerHeight + 'px' : 0)
	})
	if(mode === L2InterfaceMode.Print) {
		return (
			<L2PrintInterfaceRoot>
				<L2PrintInterfaceHeader ref={headerRef}>
					{header}
				</L2PrintInterfaceHeader>
				{children}
			</L2PrintInterfaceRoot>
		)
	}
	const fixedHeight = (mode === L2InterfaceMode.FixedHeight)
	return (
		<L2InterfaceRoot>
			<L2FixedInterfaceRoot fixedHeight={fixedHeight}>
				<L2InterfaceHeader ref={headerRef}>
					{statusBar}
					{header}
				</L2InterfaceHeader>
				{fixedHeight ? children : null}
			</L2FixedInterfaceRoot>
			<L2InterfaceHeaderShadow height={headerShadowHeight} />
			{fixedHeight ? null : children}
		</L2InterfaceRoot>
	)
}

export default L2Interface
